import { LoadingBlockFullPage } from '@/components/loading/loading-block-full-page';
import { TableHeader } from '@/components/tables/table-header';
import { CopyButton } from '@/components/ui/buttons/copy-button';
import { DrawerContainer, DrawerContent, DrawerHeader } from '@/components/ui/drawer';
import { Header } from '@/components/ui/header';
import { TabsHeader } from '@/components/ui/tabs-header';
import { useAppConfig } from '@/features/acl/acl-provider';
import { useAnalytics } from '@/features/analytics/hooks/use-analytics';
import { ExplorerView } from '@/features/explorer/components/explorer-view';
import {
    ExplorerViewProvider,
    type ExplorerState,
} from '@/features/explorer/hooks/use-explorer-query-params';
import { InventoryActions } from '@/features/inventory/components/inventory-actions';
import { InventoryDetailsAvailability } from '@/features/inventory/components/inventory-details-availability';
import { InventoryDetailsDescription } from '@/features/inventory/components/inventory-details-description';
import { InventoryDetailsRecommendations } from '@/features/inventory/components/inventory-details-recommendations';
import { InventoryDetailsVariants } from '@/features/inventory/components/inventory-details-variants';
import { useOrderColumnsForProductDetail } from '@/features/list-views/columns/use-order-columns-for-product-detail';
import { useQuoteColumnsForProductDetail } from '@/features/list-views/columns/use-quote-columns-for-product-detail';
import { DataTable } from '@/features/list-views/components/data-table';
import { useLocale } from '@/lib/hooks/use-locale';
import { cn } from '@/lib/utils';
import type { ExplorerViewHandles } from '@/server/utils/explorer-utils';
import { ExplorerTopLevelEntity } from '@/server/utils/explorer-utils';
import { api } from '@/utils/api';
import { SrSalesOrderType } from '@plato/data/starrocks/types';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useMemo, useRef, useState } from 'react';
import { InventoryDetailsInfo } from './inventory-details-info';

enum PageTabs {
    INFO = 'info',
    AVAILABILITY = 'availability',
    OPEN_QUOTES = 'quotes',
    OPEN_ORDERS = 'orders',
    EXPLORER = 'explorer',
}

type Props = {
    className?: string;
    erpProductId: string;
    customerId?: string;
    onClose?: () => void;
    onBack?: () => void;
    canAddToDraft?: boolean;
};

const InventoryDetails = ({ erpProductId, onBack, onClose, className, customerId, canAddToDraft }: Props) => {
    const { t } = useLocale();
    const { canReadOrders, canReadQuotes, canReadProductVariants } = useAppConfig();
    const [selectedTab, setSelectedTab] = useState<PageTabs>(PageTabs.INFO);
    const explorerViewRef = useRef<ExplorerViewHandles>(null);
    const [state, setState] = useState<Partial<ExplorerState>>({
        tab: ExplorerTopLevelEntity.CUSTOMER,
    });

    const sendEvent = useAnalytics();
    useEffect(() => {
        sendEvent('item_detail_page_viewed', {
            item_id: erpProductId,
        });
    }, [erpProductId, sendEvent]);

    const ordersColumns = useOrderColumnsForProductDetail();
    const quotesColumns = useQuoteColumnsForProductDetail();

    /* ============================================
     *  API Calls
     * ============================================
     */
    const {
        data: productDetails,
        isLoading: isLoadingDetail,
        isFetched: isFetchedDetail,
    } = api.platoUser.getDetailProduct.getProductDetail.useQuery({
        erpProductId,
    });

    const {
        data: ordersRawData,
        isLoading: isLoadingOrders,
        isFetchingNextPage: isFetchingOrdersNextPage,
        fetchNextPage: fetchNextPageOrders,
        isFetched: isFetchedOrders,
        hasNextPage: hasNextPageOrders,
    } = api.platoUser.getManyQuotesOrdersContainingProduct.getListWithFilters.useInfiniteQuery(
        {
            erpProductId: erpProductId,
            orderTypes: [SrSalesOrderType.ORDER],
        },
        {
            enabled: selectedTab === PageTabs.OPEN_ORDERS,
            getNextPageParam: (lastPage) => lastPage.nextCursor,
        },
    );

    const { data: ordersCount, isLoading: isLoadingOrdersCount } =
        api.platoUser.getManyQuotesOrdersContainingProduct.getCount.useQuery(
            {
                erpProductId: erpProductId,
                orderTypes: [SrSalesOrderType.ORDER],
            },
            {
                enabled: isFetchedOrders,
            },
        );

    const {
        data: quotesRawData,
        isLoading: isLoadingQuotes,
        isFetchingNextPage: isFetchingQuotesNextPage,
        fetchNextPage: fetchNextPageQuotes,
        hasNextPage: hasNextPageQuotes,
        isFetched: isFetchedQuotes,
    } = api.platoUser.getManyQuotesOrdersContainingProduct.getListWithFilters.useInfiniteQuery(
        {
            erpProductId: erpProductId,
            orderTypes: [SrSalesOrderType.QUOTE],
        },
        {
            enabled: selectedTab === PageTabs.OPEN_QUOTES,
            getNextPageParam: (lastPage) => lastPage.nextCursor,
        },
    );

    const { data: quotesCount, isLoading: isLoadingQuotesCount } =
        api.platoUser.getManyQuotesOrdersContainingProduct.getCount.useQuery(
            {
                erpProductId: erpProductId,
                orderTypes: [SrSalesOrderType.QUOTE],
            },
            {
                enabled: isFetchedQuotes,
            },
        );

    /* ============================================
     *  Clean Data
     * ============================================
     */

    const quotesData = useMemo(() => {
        if (!quotesRawData) {
            return [];
        } else {
            return quotesRawData.pages.flatMap((page) => page.result);
        }
    }, [quotesRawData]);

    const ordersData = useMemo(() => {
        if (!ordersRawData) {
            return [];
        } else {
            return ordersRawData.pages.flatMap((page) => page.result);
        }
    }, [ordersRawData]);

    /* ============================================
     *  Tables
     * ============================================
     */

    const quotesTable = useReactTable({
        data: quotesData ? quotesData : [],
        columns: quotesColumns,
        getCoreRowModel: getCoreRowModel(),
        state: {
            columnPinning: { left: ['customer_name'] },
        },
    });

    const ordersTable = useReactTable({
        data: ordersData ? ordersData : [],
        columns: ordersColumns,
        getCoreRowModel: getCoreRowModel(),
        state: {
            columnPinning: { left: ['customer_name'] },
        },
    });

    /* ============================================
     * Infinite scrolls
     * ============================================
     */

    if (isLoadingDetail || !productDetails) {
        return <LoadingBlockFullPage className="mt-64" />;
    }

    const title = `${erpProductId} ${productDetails?.product_variant_name}`;

    const canReadProductAvailability = typeof productDetails.total_availability === 'number';

    const tabs = [
        PageTabs.INFO,
        ...(canReadProductAvailability ? [PageTabs.AVAILABILITY] : []),
        ...(canReadOrders ? [PageTabs.OPEN_ORDERS] : []),
        ...(canReadQuotes ? [PageTabs.OPEN_QUOTES] : []),
        PageTabs.EXPLORER,
    ];

    return (
        <DrawerContainer className={className}>
            <div className="flex flex-col gap-3 bg-white">
                <DrawerHeader
                    className="border-b-0 pb-0"
                    title={title}
                    onBack={onBack}
                    onClose={onClose}
                    actions={
                        <>
                            <CopyButton
                                textToCopy={erpProductId}
                                title={t('copy', {
                                    ns: 'common',
                                })}
                            />
                            <InventoryActions itemTitle={title} customerId={customerId} />
                        </>
                    }
                />
                <TabsHeader<PageTabs>
                    className="px-4"
                    selectedTabKey={selectedTab}
                    onTabChange={(value) => setSelectedTab(value)}
                    tabs={tabs}
                    getTabLabel={(tab) => t(`tab_title__${tab}`, { ns: 'product-details' })}
                />
            </div>
            <DrawerContent>
                {selectedTab === PageTabs.INFO && (
                    <>
                        <InventoryDetailsInfo data={productDetails} canAddToDraft={canAddToDraft} />
                        <InventoryDetailsDescription data={productDetails} />
                        <InventoryDetailsRecommendations data={productDetails} />
                        {canReadProductVariants && (
                            <InventoryDetailsVariants
                                erpProductId={erpProductId}
                                fetchEnabled={isFetchedDetail}
                                canAddToDraft={canAddToDraft}
                            />
                        )}
                    </>
                )}
                {selectedTab === PageTabs.AVAILABILITY && (
                    <InventoryDetailsAvailability erpProductId={erpProductId} />
                )}
                {selectedTab === PageTabs.OPEN_ORDERS && (
                    <>
                        <DataTable
                            isLoading={isLoadingOrders}
                            isFetchingNextPage={isFetchingOrdersNextPage}
                            onFetchNextPage={fetchNextPageOrders}
                            hasNextPage={hasNextPageOrders}
                            totalRowsCount={ordersCount}
                            table={ordersTable}
                            columns={ordersColumns}
                        />
                        <TableHeader count={ordersCount} isLoadingCount={isLoadingOrdersCount} />
                    </>
                )}
                {selectedTab === PageTabs.OPEN_QUOTES && (
                    <>
                        <DataTable
                            isLoading={isLoadingQuotes}
                            isFetchingNextPage={isFetchingQuotesNextPage}
                            onFetchNextPage={fetchNextPageQuotes}
                            hasNextPage={hasNextPageQuotes}
                            totalRowsCount={quotesCount}
                            table={quotesTable}
                            columns={quotesColumns}
                        />
                        <TableHeader count={quotesCount} isLoadingCount={isLoadingQuotesCount} />
                    </>
                )}
                {selectedTab === PageTabs.EXPLORER && (
                    <ExplorerViewProvider
                        value={{
                            state,
                            setState,
                            defaultFilters: {
                                product: [erpProductId],
                            },
                        }}
                    >
                        <ExplorerView pageSource="product" ref={explorerViewRef} />
                    </ExplorerViewProvider>
                )}
            </DrawerContent>
        </DrawerContainer>
    );
};

export default InventoryDetails;
